/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the administer depot page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useParams } from 'react-router';

import { Theme, makeStyles } from '@material-ui/core/styles';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, FormGroup, FormControlLabel, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

import CircularProgress from '@material-ui/core/CircularProgress';

import { DateTime } from 'luxon';

import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import * as Dtos from '../../api/Neon.Dtos';
import { useBatches } from '../../store/modules/drugmanagement/batches';
import { useDepotBatch, DepotBatchActions } from '../../store/modules/drugmanagement/depotbatch';
import { AsyncForm } from '../../components/FinalForm/AsyncForm';
import { useDepotBatches } from '../../store/modules/drugmanagement/depotbatches';
import { useDepot } from '../../store/modules/drugmanagement/depot';
import { SelectWrapper } from '../../components/FinalForm/FinalFormControls/SelectWrapper';
import { CheckboxWrapper } from '../../components/FinalForm/FinalFormControls/CheckboxWrapper';
import { useSnackbar } from 'notistack';
import NumberTextField from '../../components/FinalForm/FinalFormControls/NumberTextField';
import DepotModal from './Modals/DepotModal';
import { useOrderForms } from '../../store/modules/drugmanagement/orderforms';
import DepotInformation from '../../components/DepotInformation/DepotInformation';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import { CollapsibleTable } from '@ngt/online-patient-management-ui';
import { getDrugUnitName } from '../../utilities/getDrugUnitName';
import useAsyncFunction from '../../hooks/useAsyncFunction';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IAdministerDepotParams {
    depotId?: string;
}

interface IAdministerDepotProps {
}

interface IDepotBatchModalProps {
    depotBatch: Dtos.DepotBatch | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    batches: Dtos.Batch[] | null;
    batchesLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDepotBatchForm extends Partial<Dtos.DepotBatch> {
}

interface IDepotBatchFormProps extends FormRenderProps<Dtos.DepotBatch> {
    batches: Dtos.Batch[] | null;
    batchesLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    heading: {
        marginTop: theme.spacing(4)
    },
    buttonSet: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugUnitName = getDrugUnitName(true);

/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const useDepotBatchColumns = (depotBatches: Dtos.DepotBatch[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DepotBatch>> = [
            {
                Header: 'Batch Number',
                id: 'actualBatchNo',
                accessor: db => db.batch.batchNo,
                minWidth: 155,
                maxWidth: 175
            },
            {
                Header: 'Drug Type',
                id: 'drugName',
                accessor: db => db.batch.drug.drugName,
                minWidth: 100,
                maxWidth: 150
            },
            {
                Header: 'Expiry Date',
                id: 'expiryDate',
                accessor: db => db.batch.dateExpiration,
                Cell: (props: any) => DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy'),
                minWidth: 100,
                maxWidth: 150
            },
            //{
            //    Header: `${drugUnitName} Total`,
            //    accessor: 'drugUnitsTotal',
            //    minWidth: 100,
            //    maxWidth: 150
            //},
            {
                Header: `${drugUnitName} Allocated`,
                accessor: 'drugUnitsAllocated',
                minWidth: 100,
                maxWidth: 150
            },
            {
                Header: `${drugUnitName} Destroyed`,
                accessor: 'drugUnitsDestroyed',
                minWidth: 100,
                maxWidth: 150
            },
            //{
            //    Header: `${drugUnitName} Available`,
            //    id: 'drugUnitsAvailable',
            //    accessor: db => db.drugUnitsTotal - (db.drugUnitsAllocated + db.drugUnitsDestroyed),
            //    minWidth: 100,
            //    maxWidth: 155
            //},
        ]

        return cols;
    }, []);

    return columns;
};

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IDepotBatchForm>({
    batchId: Yup.number().required().label('Batch'),
    depotId: Yup.number().required().label('Depot Batch Number'),
    drugUnitsTotal: Yup.number().required().label('Vials Total')
        .when(['drugUnitsAllocated', 'drugUnitsDestroyed'],
            (drugUnitsAllocated: number, drugUnitsDestroyed: number, schema: Yup.NumberSchema) => {
                return schema.min(drugUnitsAllocated + drugUnitsDestroyed, `${drugUnitName} total can not be less than the sum of the rest of the drug unit values`);
            }),
    drugUnitsAllocated: Yup.number().required().label('Vials Allocated'),
    drugUnitsDestroyed: Yup.number().required().label('Vials Destroyed')
});

const DepotBatchForm: React.FunctionComponent<IDepotBatchFormProps> = ({
    batches,
    batchesLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to save the depot batch.', { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const batchItems = React.useMemo(() => {
        if (!batches || batches.length === 0) {
            return [];
        }
        else {
            const availableDepotBatches = batches.filter(batch => (batch.dateExpiration ? DateTime.fromISO(batch.dateExpiration) > DateTime.now() : false) && batch.available === true);

            return batches.map(batch => {
                return <MenuItem
                    key={batch.id}
                    value={batch.id}
                    disabled={!availableDepotBatches.find(x => x.id === batch.id)}
                >
                    {batch.batchNo}
                </MenuItem>;
            });
        }
    }, [batches]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        {
                            batchesLoadState && batchesLoadState.state && batchesLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="batchId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={formDisabled}
                                    label="Batch"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {batchItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="depotBatchNo"
                            component={NumberTextField}
                            label="Depot Batch Number"
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsTotal"
                            component={NumberTextField}
                            label={`${drugUnitName} Total`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsAllocated"
                            component={NumberTextField}
                            label={`${drugUnitName} Allocated`}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsDestroyed"
                            component={NumberTextField}
                            label={`${drugUnitName} Destroyed`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Field
                                        name="available"
                                        component={CheckboxWrapper}
                                        label="Available"
                                        defaultValue={true}
                                        disabled={formDisabled}
                                    />
                                }
                                label="Available"
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DepotBatchModal: React.FunctionComponent<IDepotBatchModalProps> = ({
    depotBatch,
    modalOpen,
    setModalOpen,
    batches,
    batchesLoadState,
    onCloseClick,
    onFormSave
}) => {
    const depotBatchForm = React.useCallback((props: FormRenderProps<Dtos.DepotBatch>) => {
        return (
            <DepotBatchForm
                batches={batches}
                batchesLoadState={batchesLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [batches, batchesLoadState, setModalOpen, onFormSave]);

    const asyncSave = useAsyncFunction(DepotBatchActions.save, DepotBatchActions.saveSuccess, DepotBatchActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Depot Batch
                </DialogTitle>
                <AsyncForm
                    initialValues={depotBatch}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={depotBatchForm}
                />
            </Dialog>
        </>
    );
}

const AdministerDepot: React.FunctionComponent<IAdministerDepotProps> = () => {
    const classes = useStyles();

    const params = useParams<IAdministerDepotParams>();

    const [depot, , , depotActions] = useDepot();

    const [batches, batchesLoadState, batchesActions] = useBatches();

    const [depotBatches, depotBatchesLoadState, depotBatchesActions] = useDepotBatches();

    const [depotBatch, depotBatchLoadState, , depotBatchActions] = useDepotBatch();

    const [orderForms, orderFormLoadState, orderFormActions] = useOrderForms();

    const [depotModalOpen, setDepotModalOpen] = React.useState(false);

    const [batchModalOpen, setBatchModalOpen] = React.useState(false);

    const [depotForm, setDepot] = React.useState<Dtos.Depot | null>(null);

    const [depotBatchForm, setDepotBatch] = React.useState<Dtos.DepotBatch | null>(null);

    React.useEffect(() => {
        if (params.depotId) {
            depotActions.loadById(parseInt(params.depotId, 10));
        }

        return () => {
            depotActions.clear();
        };
    }, [params.depotId, depotActions]);

    React.useEffect(() => {
        batchesActions.load();

        return () => {
            batchesActions.clear();
        }
    }, [batchesActions]);

    React.useEffect(() => {
        if (params.depotId) {
            depotBatchesActions.loadByDepotId(parseInt(params.depotId, 10));
        }

        return () => {
            depotBatchesActions.clear();
        }
    }, [params.depotId, depotBatchesActions]);

    React.useEffect(() => {
        orderFormActions.load();

        return () => {
            orderFormActions.clear();
        }
    }, [orderFormActions]);

    React.useEffect(() => {
        if (depotBatchLoadState.state === RequestState.Success) {
            setDepotBatch(depotBatch);

            setBatchModalOpen(true);
        }
    }, [depotBatchLoadState.state, depotBatch]);

    const onDepotFormSave = React.useCallback(() => {
        if (params.depotId) {
            depotActions.loadById(parseInt(params.depotId, 10));
            depotBatchesActions.loadByDepotId(parseInt(params.depotId, 10));
        }
    }, [params.depotId, depotActions, depotBatchesActions]);

    const onBatchFormSave = React.useCallback(() => {
        depotBatchActions.clear();

        if (params.depotId) {
            depotBatchesActions.loadByDepotId(parseInt(params.depotId, 10));
        }
    }, [depotBatchActions, depotBatchesActions, params.depotId]);

    const editDepotClick = React.useCallback(() => {
        setDepot(depot);

        setDepotModalOpen(true);
    }, [depot, setDepotModalOpen]);

    const addDepotBatchClick = React.useCallback(() => {
        if (params.depotId) {
            setDepotBatch(new Dtos.DepotBatch({
                depotId: parseInt(params.depotId, 10),
                drugUnitsAllocated: 0,
                drugUnitsDestroyed: 0,
                available: true
            }));

            setBatchModalOpen(true);
        }
    }, [params.depotId, setBatchModalOpen]);

    const onRowClick = React.useCallback((row: Row<Dtos.DepotBatch>) => {
        if (row &&
            row.original) {
            depotBatchActions.clear();
            depotBatchActions.loadById(row.original.id!);
        }
    }, [depotBatchActions]);

    const depotBatchColumns = useDepotBatchColumns(depotBatches);

    //this is here for general cleanup in case something is loaded into redux and never reset
    React.useEffect(() => {
        return () => {
            depotBatchActions.clear();
        }
    }, [depotBatchActions]);

    return (
        <>
            <DepotModal
                depot={depotForm}
                modalOpen={depotModalOpen}
                setModalOpen={setDepotModalOpen}
                orderForms={orderForms}
                orderFormLoadState={orderFormLoadState}
                onFormSave={onDepotFormSave}
            />
            <DepotBatchModal
                depotBatch={depotBatchForm}
                modalOpen={batchModalOpen}
                setModalOpen={setBatchModalOpen}
                batches={batches}
                batchesLoadState={batchesLoadState}
                onFormSave={onBatchFormSave}
            />
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                        showInstitution={false}
                        showDepot={true}
                        depotCode={depot?.id?.toString()}
                        depotName={depot?.depotName}
                    />
                }
                heading="Administer Depot"
            >
                <Paper
                    className={classes.container}
                >
                    <DepotInformation
                        depot={depot}
                    />
                    <div
                        className={classes.buttonSet}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={editDepotClick}
                        >
                            Edit Depot
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={addDepotBatchClick}
                        >
                            Add Depot Batch
                        </Button>
                    </div>
                    <CollapsibleTable
                        title="Depot Batch"
                        loadState={depotBatchesLoadState}
                        data={depotBatches}
                        columns={depotBatchColumns}
                        onRowClick={onRowClick}
                    />
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default AdministerDepot;